import axios, { AxiosInstance, AxiosRequestConfig, AxiosResponse, AxiosError } from 'axios';
import { ApiError, NetworkError, TimeoutError } from './ApiError';
import { getApiBaseUrl, API_CONFIG, API_ENDPOINTS } from './config';
import { RefreshResult } from '@/application/dto/auth/RefreshResult';
import { notificationService } from '@/infrastructure/notifications';

const REFRESH_TOKEN_STORAGE_KEY = 'refreshToken';

/**
 * Extended Axios config with custom options for error handling
 */
export interface ExtendedAxiosRequestConfig extends AxiosRequestConfig {
  /**
   * Whether to show error toast notification (default: true).
   * Set to false to handle errors manually.
   */
  showErrorToast?: boolean;

  /**
   * sprint-130 S4 — set once a request has already been replayed after a silent
   * token refresh, so a still-401 replay isn't refreshed-and-retried forever.
   */
  _retried?: boolean;
}

/**
 * ApiClient — type-safe HTTP client for the MineMiner SPA.
 *
 * sprint-104 (epic-013 Sprint B, step 2d / S5). Forked from `Frontend/`'s
 * ApiClient and slimmed for the carved MineMiner.API host.
 *
 * sprint-130 S4 — sliding session. The carved host now exposes
 * `POST /api/mineminer/auth/refresh`, so a 401 triggers a single silent token
 * refresh and the original request is replayed transparently (the in-progress
 * booth form + staged photos survive because the page never unmounts). Only when
 * the refresh itself fails (refresh token also expired) do we clear and redirect
 * to `/login`. A single in-flight refresh promise de-duplicates concurrent 401s.
 *
 *   - Retains: bearer-token request interceptor, retry-on-transient,
 *     typed error envelope, optional toast suppression.
 */
export class ApiClient {
  private readonly client: AxiosInstance;
  private readonly baseURL: string;

  // sprint-130 S4 — a single in-flight refresh shared by all concurrent 401s.
  private refreshInFlight: Promise<boolean> | null = null;

  // sprint-130 S4 — lets the auth store keep `tokenExpiresAt` in sync when a
  // refresh happens outside the login flow (avoids an ApiClient→store import cycle).
  private onTokensRefreshed: ((expiresAt: string) => void) | null = null;

  constructor(baseURL?: string) {
    this.baseURL = baseURL ?? getApiBaseUrl();
    this.client = axios.create({
      baseURL: this.baseURL,
      timeout: API_CONFIG.TIMEOUT,
      headers: {
        'Content-Type': 'application/json',
      },
    });

    this.setupInterceptors();
  }

  private setupInterceptors(): void {
    // Request interceptor — attach bearer token.
    this.client.interceptors.request.use(
      (config) => {
        const token = this.getStoredToken();
        if (token !== null && typeof config.headers !== 'undefined') {
          config.headers.Authorization = `Bearer ${token}`;
        }
        return config;
      },
      (error: unknown) => Promise.reject(this.handleError(error, undefined)),
    );

    // Response interceptor — on 401, attempt a single silent refresh and replay
    // the original request; only fall through to clear-and-redirect if refresh fails.
    this.client.interceptors.response.use(
      (response: AxiosResponse) => response,
      async (error: AxiosError) => {
        const originalRequest = error.config as ExtendedAxiosRequestConfig | undefined;

        const isAuthRetryable =
          error.response?.status === 401 &&
          originalRequest !== undefined &&
          !this.isLoginRequest(originalRequest) &&
          !this.isRefreshRequest(originalRequest);

        if (isAuthRetryable && originalRequest !== undefined && originalRequest._retried !== true) {
          originalRequest._retried = true;
          const refreshed = await this.refreshSession();
          if (refreshed) {
            // The request interceptor re-attaches the freshly stored token on replay.
            return this.client(originalRequest);
          }
          this.failAuth();
        } else if (isAuthRetryable) {
          // Already retried once (or replay still 401) — give up and redirect.
          this.failAuth();
        }

        return Promise.reject(this.handleError(error, originalRequest));
      },
    );
  }

  // sprint-130 S4 — clear both tokens and bounce to /login (the terminal auth
  // failure: the refresh token has also expired).
  private failAuth(): void {
    this.clearStoredToken();
    this.clearStoredRefreshToken();
    if (typeof window !== 'undefined' && window.location.pathname !== '/login') {
      window.location.href = '/login';
    }
  }

  private isLoginRequest(config: AxiosRequestConfig | undefined): boolean {
    return config?.url?.includes(API_ENDPOINTS.AUTH.LOGIN) ?? false;
  }

  private isRefreshRequest(config: AxiosRequestConfig | undefined): boolean {
    return config?.url?.includes(API_ENDPOINTS.AUTH.REFRESH) ?? false;
  }

  /**
   * sprint-130 S4 — exchange the stored access + refresh tokens for a rotated
   * pair. De-duplicates concurrent callers behind a single in-flight promise.
   * Returns true when new tokens were stored, false otherwise (caller redirects).
   * Performed with a bare axios call so it bypasses this client's interceptors.
   */
  refreshSession(): Promise<boolean> {
    if (this.refreshInFlight === null) {
      this.refreshInFlight = this.performRefresh().finally(() => {
        this.refreshInFlight = null;
      });
    }
    return this.refreshInFlight;
  }

  private async performRefresh(): Promise<boolean> {
    const accessToken = this.getStoredToken();
    const refreshToken = this.getStoredRefreshToken();
    if (accessToken === null || refreshToken === null) {
      return false;
    }

    try {
      const response = await axios.post<RefreshResult>(
        `${this.baseURL}${API_ENDPOINTS.AUTH.REFRESH}`,
        { accessToken, refreshToken },
        {
          headers: { 'Content-Type': 'application/json' },
          timeout: API_CONFIG.TIMEOUT,
        },
      );

      const result = response.data;
      if (!result.succeeded || !result.accessToken) {
        return false;
      }

      this.setStoredToken(result.accessToken);
      this.setStoredRefreshToken(result.refreshToken);
      this.onTokensRefreshed?.(result.expiresAt);
      return true;
    } catch {
      return false;
    }
  }

  /**
   * sprint-130 S4 — registered by the auth store so a silent refresh keeps the
   * store's `tokenExpiresAt` (and thus the session-timeout warning) in sync.
   */
  registerTokensRefreshedCallback(callback: (expiresAt: string) => void): void {
    this.onTokensRefreshed = callback;
  }

  private getStoredToken(): string | null {
    if (typeof window === 'undefined') {
      return null;
    }
    return localStorage.getItem('authToken');
  }

  private setStoredToken(token: string): void {
    if (typeof window === 'undefined') {
      return;
    }
    localStorage.setItem('authToken', token);
  }

  private clearStoredToken(): void {
    if (typeof window === 'undefined') {
      return;
    }
    localStorage.removeItem('authToken');
  }

  private getStoredRefreshToken(): string | null {
    if (typeof window === 'undefined') {
      return null;
    }
    return localStorage.getItem(REFRESH_TOKEN_STORAGE_KEY);
  }

  private setStoredRefreshToken(token: string): void {
    if (typeof window === 'undefined') {
      return;
    }
    localStorage.setItem(REFRESH_TOKEN_STORAGE_KEY, token);
  }

  private clearStoredRefreshToken(): void {
    if (typeof window === 'undefined') {
      return;
    }
    localStorage.removeItem(REFRESH_TOKEN_STORAGE_KEY);
  }

  private showErrorNotification(error: Error, config?: ExtendedAxiosRequestConfig): void {
    const showToast = config?.showErrorToast ?? true;
    if (!showToast) {
      return;
    }

    // Don't toast the 401 that triggers the login redirect — the user will
    // see the login screen immediately.
    if (ApiError.isApiError(error) && error.statusCode === 401) {
      return;
    }

    notificationService.showError(error.message || 'Request failed');
  }

  private handleError(error: unknown, config?: ExtendedAxiosRequestConfig): Error {
    let typedError: Error;

    if (axios.isAxiosError(error)) {
      const axiosError = error as AxiosError<{
        message?: string;
        errors?: Record<string, string[]> | string[];
        error?: string;
        traceId?: string;
      }>;

      if (!axiosError.response) {
        if (axiosError.code === 'ECONNABORTED' || axiosError.message.includes('timeout')) {
          typedError = new TimeoutError();
        } else {
          typedError = new NetworkError();
        }
      } else {
        const { status, data } = axiosError.response;
        let message = data?.message ?? data?.error ?? axiosError.message;
        let errors: Record<string, string[]> | undefined;
        const traceId = data?.traceId;

        if (data?.errors) {
          if (Array.isArray(data.errors)) {
            const flatErrors = data.errors;
            if (flatErrors.length > 0) {
              const firstError = flatErrors[0];
              if (firstError !== undefined && message === axiosError.message) {
                message = firstError;
              }
            }
            errors = { general: flatErrors };
          } else {
            errors = data.errors;
          }
        }

        typedError = new ApiError(message, status, errors, traceId);
      }
    } else if (error instanceof Error) {
      typedError = error;
    } else {
      typedError = new Error('An unknown error occurred');
    }

    this.showErrorNotification(typedError, config);
    return typedError;
  }

  private async retryRequest<T>(
    requestFn: () => Promise<T>,
    retries: number = API_CONFIG.MAX_RETRIES,
  ): Promise<T> {
    try {
      return await requestFn();
    } catch (error) {
      if (retries > 0 && this.shouldRetry(error)) {
        const delay = API_CONFIG.RETRY_DELAY * (API_CONFIG.MAX_RETRIES - retries + 1);
        await new Promise((resolve) => setTimeout(resolve, delay));
        return this.retryRequest(requestFn, retries - 1);
      }
      throw error;
    }
  }

  private shouldRetry(error: unknown): boolean {
    if (ApiError.isApiError(error)) {
      const retryableCodes: number[] = [...API_CONFIG.RETRY_STATUS_CODES];
      return retryableCodes.includes(error.statusCode);
    }
    if (NetworkError.isNetworkError(error) || TimeoutError.isTimeoutError(error)) {
      return true;
    }
    return false;
  }

  async get<T>(url: string, config?: ExtendedAxiosRequestConfig): Promise<T> {
    return this.retryRequest(async () => {
      const response = await this.client.get<T>(url, config);
      return response.data;
    });
  }

  /**
   * sprint-276 (TD-255) — bytes plus response headers. Deliberately NOT through retryRequest: the
   * one caller (the evidence image preview) hits an endpoint that writes an audit row per call, so
   * a silent retry would record an access nobody made.
   */
  async getBlob(
    url: string,
    config?: ExtendedAxiosRequestConfig,
  ): Promise<{ data: Blob; headers: Record<string, unknown> }> {
    const response = await this.client.get<Blob>(url, { ...config, responseType: 'blob' });
    return { data: response.data, headers: response.headers as Record<string, unknown> };
  }

  async post<T>(url: string, data?: unknown, config?: ExtendedAxiosRequestConfig): Promise<T> {
    const response = await this.client.post<T>(url, data, config);
    return response.data;
  }

  async postForm<T>(
    url: string,
    formData: FormData,
    config?: ExtendedAxiosRequestConfig,
  ): Promise<T> {
    const response = await this.client.post<T>(url, formData, {
      ...config,
      headers: {
        ...config?.headers,
        'Content-Type': undefined,
      },
    });
    return response.data;
  }

  async put<T>(url: string, data?: unknown, config?: ExtendedAxiosRequestConfig): Promise<T> {
    const response = await this.client.put<T>(url, data, config);
    return response.data;
  }

  async patch<T>(url: string, data?: unknown, config?: ExtendedAxiosRequestConfig): Promise<T> {
    const response = await this.client.patch<T>(url, data, config);
    return response.data;
  }

  async delete<T>(url: string, config?: ExtendedAxiosRequestConfig): Promise<T> {
    const response = await this.client.delete<T>(url, config);
    return response.data;
  }

  setAuthToken(token: string): void {
    this.setStoredToken(token);
  }

  // sprint-130 S4 — persist the refresh token alongside the access token so the
  // 401-retry interceptor and proactive monitor can slide the session.
  setRefreshToken(token: string): void {
    this.setStoredRefreshToken(token);
  }

  clearAuthToken(): void {
    this.clearStoredToken();
    this.clearStoredRefreshToken();
  }
}

/**
 * Default API client instance
 */
export const apiClient = new ApiClient();
